import { useState } from "react";
import { Link } from "react-router";
import { Clock, BookOpen, Headphones, BookText } from "lucide-react";
import LanguageSelector from "../../../../shared/ui/header/LanguageSelector";
import { usePublicMeditations } from "../../application/usePublicMeditations";
import { PublicMeditation, PublicMeditationKind } from "../../domain/types";

function formatDuration(seconds: number): string {
    const mins = Math.round(seconds / 60);
    return `${mins} min`;
}

function KindIcon({ kind }: { kind: PublicMeditationKind }) {
    if (kind === PublicMeditationKind.AUDIO) return <Headphones className="w-4 h-4" />;
    if (kind === PublicMeditationKind.TEXT_AND_AUDIO) return <BookText className="w-4 h-4" />;
    return <BookOpen className="w-4 h-4" />;
}

function MeditationCard({
    meditation,
    selected,
    onSelect,
}: {
    meditation: PublicMeditation;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            onClick={onSelect}
            className={[
                "w-full text-left rounded-xl border p-4 transition-colors",
                selected
                    ? "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30"
                    : "border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700 bg-white dark:bg-gray-800/50",
            ].join(" ")}
        >
            <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-sm text-content-primary">
                    {meditation.title}
                </span>
                <span className="shrink-0 flex items-center gap-1 text-xs text-content-tertiary mt-0.5">
                    <Clock className="w-3.5 h-3.5" />
                    {formatDuration(meditation.declaredDurationSeconds ?? meditation.estimatedSeconds)}
                </span>
            </div>
            {meditation.description && (
                <p className="mt-1 text-xs text-content-secondary line-clamp-2">
                    {meditation.description}
                </p>
            )}
        </button>
    );
}

function MeditationDetail({ meditation }: { meditation: PublicMeditation }) {
    return (
        <div className="flex flex-col gap-6">
            <div>
                <h2 className="text-xl font-semibold text-content-primary">
                    {meditation.title}
                </h2>
                <div className="mt-1 flex items-center gap-3 text-sm text-content-tertiary">
                    <span className="flex items-center gap-1">
                        <KindIcon kind={meditation.kind} />
                        {meditation.kind === PublicMeditationKind.TEXT ? "Texto" :
                         meditation.kind === PublicMeditationKind.AUDIO ? "Audio" : "Texto y audio"}
                    </span>
                    <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(meditation.declaredDurationSeconds ?? meditation.estimatedSeconds)}
                    </span>
                    {meditation.authorName && (
                        <span>por {meditation.authorName}</span>
                    )}
                </div>
                {meditation.description && (
                    <p className="mt-3 text-sm text-content-secondary">
                        {meditation.description}
                    </p>
                )}
            </div>

            {meditation.kind !== PublicMeditationKind.AUDIO && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6">
                    <p className="text-sm text-content-tertiary uppercase tracking-wider font-medium mb-4">
                        Guión
                    </p>
                    <div className="text-sm text-content-primary leading-relaxed whitespace-pre-line font-serif">
                        {/* Script is intentionally not exposed in the public API response — this placeholder will show until audio is added */}
                        <p className="text-content-tertiary italic">
                            Selecciona esta meditación para comenzar. El guión completo se mostrará aquí durante la sesión.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function MeditationLibraryPage() {
    const { data: meditations, isLoading, isError } = usePublicMeditations();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const selected = meditations?.find((m) => m.id === selectedId) ?? null;

    return (
        <div className="min-h-screen bg-surface-base text-content-primary flex flex-col">
            <header className="border-b border-gray-200 dark:border-gray-800">
                <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="shrink-0">
                        <img
                            src="/images/logo/logo.png"
                            alt="Alchely"
                            className="h-8 w-auto dark:brightness-0 dark:invert"
                        />
                    </Link>
                    <div className="flex items-center gap-2">
                        <LanguageSelector />
                        <Link
                            to="/login"
                            className="text-sm text-content-secondary hover:text-content-primary transition-colors"
                        >
                            Iniciar sesión
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 py-10">
                <div className="mb-8">
                    <h1 className="text-2xl font-bold text-content-primary">
                        Meditaciones
                    </h1>
                    <p className="mt-1 text-sm text-content-secondary">
                        Meditaciones guiadas de Alchely para tu bienestar.
                    </p>
                </div>

                {isLoading && (
                    <div className="flex items-center justify-center py-20 text-content-tertiary text-sm">
                        Cargando meditaciones…
                    </div>
                )}

                {isError && (
                    <div className="flex items-center justify-center py-20 text-red-500 text-sm">
                        No se pudieron cargar las meditaciones. Inténtalo de nuevo más tarde.
                    </div>
                )}

                {!isLoading && !isError && meditations && (
                    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
                        <div className="flex flex-col gap-2">
                            {meditations.map((m) => (
                                <MeditationCard
                                    key={m.id}
                                    meditation={m}
                                    selected={m.id === selectedId}
                                    onSelect={() => setSelectedId(m.id)}
                                />
                            ))}
                        </div>

                        <div>
                            {selected ? (
                                <MeditationDetail meditation={selected} />
                            ) : (
                                <div className="flex items-center justify-center h-48 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 text-sm text-content-tertiary">
                                    Selecciona una meditación para empezar
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
